import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Selecto, { type ElementType, type OnSelectEvent, type SelectoMethods } from 'react-selecto';

import {
  type SwimlaneEventListener,
  useSwimlaneApiMethod,
  useSwimlaneEventPublisher,
  useSwimlaneInternalEventHandler,
  useSwimlanePrivateApi,
} from '../api';
import type { ICardProps, TCardId } from '../types';
import { buildCardId, parseCardId } from '../utils';

type SelectoProps = ComponentProps<typeof Selecto>;
type CardIds = Set<TCardId>;
type SetSelectedCards = (cardIds: CardIds) => void;
type ClearSelection = () => void;

const CARD_ID_ATTRIBUTE = 'data-swimlane-card-selection-id';
const SELECTION_DISABLED_ATTRIBUTE = 'data-swimlane-card-selection-disabled';
const EMPTY_SELECTION_SET: CardIds = new Set<TCardId>();

export const CardsSelectionContext = createContext<CardIds>(EMPTY_SELECTION_SET);

const getSelectableCardId = (element?: ElementType) => element?.getAttribute(CARD_ID_ATTRIBUTE);
const getElementsCardIds = (elements: ElementType[]) =>
  elements.map(getSelectableCardId).filter(Boolean) as string[];
const addElementsToSelection = (selectedCardIds: CardIds, elements: ElementType[]) => {
  if (!elements.length) {
    return selectedCardIds;
  }

  const cardIdsToAdd = getElementsCardIds(elements);
  // Set.union is not working with the current setup
  return cardIdsToAdd.length ? new Set([...selectedCardIds, ...cardIdsToAdd]) : selectedCardIds;
};
const removeElementsFromSelection = (selectedCardIds: CardIds, elements: ElementType[]) => {
  if (!elements.length) {
    return selectedCardIds;
  }

  const cardIdsToRemove = new Set(getElementsCardIds(elements));
  // Set.difference is not working with the current setup
  return cardIdsToRemove.size
    ? new Set([...selectedCardIds].filter((id) => !cardIdsToRemove.has(id)))
    : selectedCardIds;
};
const continueSelectionByClick = (
  element: ElementType,
  selectedCardIds: CardIds,
  setSelectedCards: SetSelectedCards,
) => {
  const cardId = getSelectableCardId(element);
  if (cardId) {
    const nextSelectedCardIds = selectedCardIds.has(cardId)
      ? removeElementsFromSelection(selectedCardIds, [element])
      : addElementsToSelection(selectedCardIds, [element]);
    setSelectedCards(nextSelectedCardIds);
  }
};
const shouldContinueSelect = (event: MouseEvent) => Boolean(event?.ctrlKey || event?.shiftKey);

type CardSelectionProviderProps = {
  children: ReactNode;
  readOnly?: boolean;
  container?: HTMLElement;
  maxSelectedCards?: number;
};

export const CardsSelectionProvider = ({
  children,
  readOnly = false,
  container = document.body,
  maxSelectedCards = Number.MAX_SAFE_INTEGER,
}: CardSelectionProviderProps) => {
  const [selectedCardIds, setSelectedCardIds] = useState<CardIds>(EMPTY_SELECTION_SET);
  const selectedCardIdsRef = useRef(selectedCardIds);
  const selectoRef = useRef(null);
  const isDragFinishedRef = useRef<boolean>(false);
  const privateApiRef = useSwimlanePrivateApi();
  const publishEvent = useSwimlaneEventPublisher();

  const getSelectedCardIds = useCallback(() => selectedCardIdsRef.current, []);
  const clearSelection = useCallback<ClearSelection>(
    () => setSelectedCardIds(EMPTY_SELECTION_SET),
    [],
  );
  const setSelectedCards = useCallback<SetSelectedCards>(
    (cardIds: CardIds) => {
      if (!cardIds?.size) {
        return clearSelection();
      }

      let normalizedCardIds = cardIds;

      if (cardIds.size > maxSelectedCards) {
        publishEvent('maxSelectedCardsReached', {
          selectedCardsCount: cardIds.size,
          maxSelectedCards,
        });
        normalizedCardIds = new Set([...cardIds].slice(0, maxSelectedCards));
      }

      setSelectedCardIds((prevSelectedCardIds) => {
        if (
          prevSelectedCardIds.size === normalizedCardIds.size &&
          [...prevSelectedCardIds].every((id) => normalizedCardIds.has(id))
        ) {
          return prevSelectedCardIds;
        }

        return normalizedCardIds;
      });
    },
    [maxSelectedCards, publishEvent, clearSelection],
  );

  useSwimlaneApiMethod(privateApiRef, { getSelectedCardIds, clearSelection }, 'public');

  const onCardDragEnd = useCallback<SwimlaneEventListener<'cardsDragEnd'>>(() => {
    isDragFinishedRef.current = true;
  }, []);

  useSwimlaneInternalEventHandler('cardsDragEnd', onCardDragEnd);

  const onSelectEnd = useCallback(
    (event: OnSelectEvent) => {
      // Keep selection on drag end
      if (isDragFinishedRef.current) {
        isDragFinishedRef.current = false;
        clearSelection();
        return;
      }

      const { selected, inputEvent, rect } = event;
      const isClickEvent = !rect.width && !rect.height;

      // Selecto `toggleContinueSelect` option is not working with `clickBySelectEnd` set to true
      if (shouldContinueSelect(inputEvent)) {
        if (!selected.length) {
          return;
        }

        if (isClickEvent) {
          const firstElement = selected[0];
          if (firstElement) {
            return continueSelectionByClick(
              firstElement,
              selectedCardIdsRef.current,
              setSelectedCards,
            );
          }
        } else {
          return setSelectedCards(addElementsToSelection(selectedCardIdsRef.current, selected));
        }
      }

      if (isClickEvent || !selected.length) {
        return clearSelection();
      }

      setSelectedCards(new Set(getElementsCardIds(selected)));
    },
    [setSelectedCards, clearSelection],
  );

  useEffect(() => {
    if (readOnly) {
      clearSelection();
    }
  }, [readOnly, clearSelection]);

  useEffect(() => {
    publishEvent('selectionChange', {
      selectedCardIds,
      selectedItemIds: new Set([...selectedCardIds].map((cardId) => parseCardId(cardId).itemID)),
    });

    let selectedCards: ElementType[] = [];

    if (selectedCardIds.size) {
      const cssSelector = [...selectedCardIds]
        .map((id) => `[${CARD_ID_ATTRIBUTE}="${id}"]`)
        .join(', ');
      selectedCards = [...container.querySelectorAll(cssSelector)] as ElementType[];
    }

    selectedCardIdsRef.current = selectedCardIds;
    (selectoRef.current as unknown as SelectoMethods)?.setSelectedTargets(selectedCards);
  }, [selectedCardIds, container, publishEvent]);

  useEffect(() => {
    const handleScroll = () => (selectoRef.current as unknown as SelectoMethods)?.checkScroll();
    const handleClick = (event: MouseEvent) => {
      if (shouldContinueSelect(event)) {
        event.stopPropagation();
      }
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('click', handleClick);
    };
  }, [container]);

  const { scrollOptions, onScroll } = useMemo<Pick<SelectoProps, 'scrollOptions' | 'onScroll'>>(
    () => ({
      scrollOptions: {
        container,
        getScrollPosition: () => [container.scrollLeft, container.scrollTop],
        throttleTime: 30,
        threshold: 30,
      },
      onScroll: ({ direction }) =>
        container.scrollBy((direction[0] ?? 0) * 100, (direction[1] ?? 0) * 100),
    }),
    [container],
  );

  const onDragStart = useCallback<Exclude<SelectoProps['onDragStart'], undefined>>((event) => {
    const eventTarget = event.inputEvent.target as HTMLElement;
    if (eventTarget?.hasAttribute(SELECTION_DISABLED_ATTRIBUTE)) {
      event.stop();
    }
  }, []);

  return (
    <>
      {!readOnly && (
        <Selecto
          ref={selectoRef}
          container={container}
          dragContainer={container}
          selectableTargets={[`[${CARD_ID_ATTRIBUTE}]`]}
          // toggleContinueSelect={[['ctrl'], ['shift']]} // this option is not working with `clickBySelectEnd` set to true
          keyContainer={window}
          onSelectEnd={onSelectEnd}
          onScroll={onScroll}
          onDragStart={onDragStart}
          scrollOptions={scrollOptions}
          hitRate={5}
          selectFromInside={false}
          selectByClick
          clickBySelectEnd
        />
      )}
      <CardsSelectionContext.Provider value={selectedCardIds}>
        {children}
      </CardsSelectionContext.Provider>
    </>
  );
};

CardsSelectionProvider.displayName = 'CardsSelectionProvider';

export const useIsCardSelected = (props: ICardProps) =>
  useContext(CardsSelectionContext).has(buildCardId(props));

export const bindCardSelection = (props: ICardProps) => ({
  [CARD_ID_ATTRIBUTE]: buildCardId(props),
});

export const bindCardSelectionDisabled = () => ({
  [SELECTION_DISABLED_ATTRIBUTE]: '',
});
